import { Z } from "aldoo-ra/ZLayers"
import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react"
import { motion } from "framer-motion"
import { createPortal } from "react-dom"
import { twMerge } from "tailwind-merge"
import VariantReader from "aldoo-ra/VariantReader"

const Notification = forwardRef(
  ({ defaultVariant = "default", styleOverrides = {} }, ref) => {
    const [notification, setNotification] = useState({
      message: "",
      className: "",
      onRender: null,
      variant: defaultVariant, // Add variant property with default
    })
    const [isVisible, setIsVisible] = useState(false)
    const timeoutRef = useRef(null)
    const [isClient, setIsClient] = useState(false)

    // Use the notification's variant instead of props.variant
    const variants = VariantReader.useVariant({
      componentName: "Notification",
      variant: notification.variant,
      styleOverrides,
    })

    useEffect(() => {
      // Only turn on client-specific rendering after mounting
      setIsClient(true)
    }, [])

    const closeNotification = () => {
      setIsVisible(false)
    }

    // Update the show function to accept variant parameter
    const show = ({
      message,
      className = "",
      onRender = null,
      variant,
      type, // Accept type for backwards compatibility
    }) => {
      // Use type as variant if variant is not provided
      const finalVariant = variant || type || defaultVariant
      setNotification({ message, className, onRender, variant: finalVariant })
      setIsVisible(true)
      clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => {
        setIsVisible(false)
      }, 5000) // Auto-hide after 5 seconds
    }

    useImperativeHandle(ref, () => ({
      show,
    }))

    useEffect(() => {
      return () => {
        clearTimeout(timeoutRef.current)
      }
    }, [])

    // Determine what to render based on whether onRender is provided
    const renderNotificationContent = () => {
      if (notification.onRender) {
        // Pass variants to the custom render function along with other props
        return notification.onRender({
          closeNotification,
          message: notification.message,
          variants, // Pass the variants object
        })
      }

      // Default notification UI
      return (
        <motion.div
          className={twMerge(variants.notification, notification.className)}
          initial={{ y: -20 }}
          animate={{ y: 0 }}
          exit={{ y: -20 }}
          transition={{ duration: 0.3 }}
        >
          <span className={variants.message}>{notification.message}</span>
          <button
            className={twMerge(variants.closeButton)}
            onClick={closeNotification}
          >
            ×
          </button>
        </motion.div>
      )
    }

    return isClient
      ? createPortal(
          <div>
            {isVisible && (
              <div
                className={twMerge(variants.container)}
                style={{ zIndex: Z.toast }}
              >
                {renderNotificationContent()}
              </div>
            )}
          </div>,
          document.body
        )
      : null
  }
)

Notification.displayName = "Notification"

// Create a singleton instance of the notification component
let notificationInstance = null

export const showNotification = (config) => {
  if (notificationInstance) {
    // Support both string and object formats
    // If config is a string, convert it to { message: config }
    const normalizedConfig = typeof config === 'string'
      ? { message: config }
      : config

    notificationInstance.show(normalizedConfig)
  }
}

// Higher-order component to inject the notification instance
const NotificationProvider = ({ variant = "default", styleOverrides = {} }) => {
  const notificationRef = useRef()

  useEffect(() => {
    notificationInstance = notificationRef.current
  }, [])

  return (
    <Notification
      ref={notificationRef}
      defaultVariant={variant} // Changed from variant to defaultVariant
      styleOverrides={styleOverrides}
    />
  )
}

export default NotificationProvider
